import React, { useState } from "react";
import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Slider from "@material-ui/core/Slider";
import Checkbox from "@material-ui/core/Checkbox";
import { makeStyles } from "@material-ui/core/styles";
import { P } from "./constants";
import { Kpi, Card } from "./ui";

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: 12 },
  kpiGrid: { display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 8 },
  // Pill-style segmented tab control (matches the rank toggle on the Network tab):
  // inline, self-start, soft container with a brand-indigo selected pill.
  tabStrip: { display: "inline-flex", alignSelf: "flex-start", gap: 2, background: "var(--color-background-secondary)", borderRadius: 7, padding: 3, border: "0.5px solid var(--color-border-tertiary)" },
  // Pill button: color/weight/background are per-button (active state) so they
  // stay inline; the constant casing/padding/radius live here.
  tabBtn: { textTransform: "none", minWidth: 0, padding: "5px 16px", borderRadius: 5 },

  drivoList: { display: "flex", flexDirection: "column", gap: 8 },
  drivoHeaderRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 },
  drivoHeaderTitle: { fontWeight: 600, color: "var(--color-text-primary)" },
  drivoHeaderInfo: { color: "var(--color-text-secondary)" },
  drivoHeaderHi: { fontWeight: 600, color: P.nv },
  drivoCard: { background: "var(--color-background-primary)", border: "0.5px solid var(--color-border-tertiary)", borderRadius: 10, overflow: "hidden" },
  drivoGrid: { display: "grid", gridTemplateColumns: "4px 1fr" },
  drivoBody: { padding: "12px 16px" },
  drivoTopRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 },
  drivoNameRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 3 },
  drivoName: { fontWeight: 700, color: "var(--color-text-primary)" },
  drivoBadge: { fontWeight: 700, color: "#fff", padding: "1px 6px", borderRadius: 3 },
  badgeNv: { background: P.nv },
  badgePd: { background: "#241C8A" },
  badgeBl: { background: "#3127B8" },
  drivoDesc: { color: "var(--color-text-secondary)" },
  drivoRevCol: { textAlign: "right", flexShrink: 0, marginLeft: 20 },
  drivoRevLabel: { color: "var(--color-text-tertiary)" },
  drivoRevVal: { fontWeight: 700, color: P.nv, fontFamily: "var(--font-mono)" },
  drivoRevChg: { color: "#241C8A" },
  drivoStatGrid: { display: "grid", gridTemplateColumns: "repeat(6,minmax(0,1fr))", gap: 8 },
  drivoStatTile: { background: "var(--color-background-secondary)", borderRadius: 6, padding: "7px 9px" },
  drivoStatLabel: { color: "var(--color-text-tertiary)", marginBottom: 2 },
  drivoStatVal: { fontWeight: 700, fontFamily: "var(--font-mono)" },

  aggGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  rankRow: { padding: "10px 0", borderBottom: "0.5px solid var(--color-border-tertiary)" },
  rankTopRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 5 },
  rankLeft: { display: "flex", alignItems: "center", gap: 8 },
  rankNum: { fontWeight: 700, width: 16 },
  rankNameRow: { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" },
  rankName: { fontWeight: 600 },
  rankTag: { fontWeight: 600, color: "#fff", padding: "1px 5px", borderRadius: 3 },
  rankPaused: { color: "var(--color-text-tertiary)", background: "var(--color-background-secondary)", padding: "1px 5px", borderRadius: 3 },
  rankWeekend: { color: "#2E7D32", background: "#E6F4E8", padding: "1px 5px", borderRadius: 3 },
  rankNote: { color: "var(--color-text-tertiary)", marginTop: 1 },
  rankRevCol: { textAlign: "right", flexShrink: 0 },
  rankRevVal: { fontWeight: 700, color: P.nv, fontFamily: "var(--font-mono)" },
  rankRevSub: { color: "var(--color-text-tertiary)" },
  rankBarTrack: { height: 3, borderRadius: 2, background: "var(--color-background-secondary)", marginBottom: 5 },
  rankBarFill: { height: "100%", background: P.nv, borderRadius: 2 },
  rankFlag: { background: "#E6F4E8", borderRadius: 5, padding: "5px 9px", border: "0.5px solid #BFE3C6" },
  rankFlagText: { color: "#2E7D32" },

  rightCol: { display: "flex", flexDirection: "column", gap: 12 },
  aggItem: { paddingBottom: 12, marginBottom: 12, borderBottom: "0.5px solid var(--color-border-tertiary)" },
  aggHeaderRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 },
  aggName: { fontWeight: 600, color: P.nv },
  aggControls: { display: "flex", alignItems: "center", gap: 12 },
  weekendLabel: { display: "flex", alignItems: "center", gap: 5, cursor: "pointer" },
  weekendText: { color: "var(--color-text-secondary)" },
  // Active/Paused toggle: colors depend on state so they stay inline; constant
  // casing/weight live here.
  toggleBtn: { textTransform: "none", padding: "3px 9px", borderRadius: 4, fontWeight: 600, minWidth: 0 },
  sliderGrid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 },
  sliderGrid2b: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 },
  sliderGrid3: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 },
  sliderHeadRow: { display: "flex", justifyContent: "space-between", marginBottom: 2 },
  sliderLabel: { color: "var(--color-text-secondary)" },
  sliderVal: { fontWeight: 700, color: P.nv, fontFamily: "var(--font-mono)" },
  slider: { color: P.nv },
  checkbox: { color: P.nv, padding: 0, "&.Mui-checked": { color: P.nv } },

  partnerItem: { paddingBottom: 10, marginBottom: 10, borderBottom: "0.5px solid var(--color-border-tertiary)" },
  partnerHeaderRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 },
  partnerName: { fontWeight: 600, color: P.nv },
  partnerSub: { color: "var(--color-text-tertiary)" },
  partnerControls: { display: "flex", alignItems: "center", gap: 10 },
  addBtn: { textTransform: "none", width: "100%", padding: "7px 0", background: "var(--color-background-secondary)", border: "0.5px solid var(--color-border-secondary)", borderRadius: 6, color: "var(--color-text-secondary)" },
});

export default function ChannelsTab({ period }) {
  const cls = useStyles();
  const DRIVO_SOURCES = [
    {id:"preemail",  name:"Pre-booking Email",    bk:211, rv:5275, avg:25, chg:31, conv:18.4, timeOnPage:null, upsells:82, upsellRev:2460, desc:"Automated email sent 3 days before arrival"},
    {id:"canary",    name:"Canary",               bk:142, rv:4970, avg:35, chg:22, conv:24.1, timeOnPage:3.2,  upsells:61, upsellRev:2135, desc:"Pre-stay upsell via Canary platform"},
    {id:"pms",       name:"PMS / Direct",         bk:318, rv:11130,avg:35, chg:5,  conv:null, timeOnPage:null, upsells:44, upsellRev:1540, desc:"Front desk booking via PMS"},
    {id:"qr_room",   name:"QR Code -- In Room",    bk:44,  rv:1320, avg:30, chg:41, conv:31.7, timeOnPage:4.8,  upsells:31, upsellRev:930,  desc:"QR card placed in hotel room"},
    {id:"bookmarks", name:"Bookmarks",            bk:88,  rv:2640, avg:30, chg:18, conv:14.2, timeOnPage:2.1,  upsells:18, upsellRev:540,  desc:"Digital concierge bookmarks"},
    {id:"qr_desk",   name:"QR Code -- Front Desk", bk:67,  rv:1675, avg:25, chg:8,  conv:9.8,  timeOnPage:1.4,  upsells:8,  upsellRev:200,  desc:"Scannable QR at check-in desk"},
  ];
  const [aggregators, setAggregators] = useState([
    {id:"parkhound",  name:"Parkhound",   type:"aggregator", rate:22, minRate:18, maxPerDay:12, maxPerMonth:240, bk:82,  rv:1804, minPctBk:68, active:true,  blockWeekend:false, note:"Daily parking aggregator"},
    {id:"carparkit",  name:"Carparkit",   type:"aggregator", rate:20, minRate:16, maxPerDay:8,  maxPerMonth:160, bk:37,  rv:740,  minPctBk:91, active:true,  blockWeekend:false, note:"Daily & monthly parking"},
    {id:"divvy",      name:"Divvy",       type:"aggregator", rate:18, minRate:15, maxPerDay:6,  maxPerMonth:120, bk:24,  rv:432,  minPctBk:88, active:true,  blockWeekend:true,  note:"Monthly parking specialist"},
    {id:"hotel_ibis", name:"Ibis Sydney", type:"partner",    rate:28, minRate:28, maxPerDay:10, maxPerMonth:200, bk:31,  rv:868,  minPctBk:12, active:true,  blockWeekend:false, note:"Nearby hotel overflow partner"},
    {id:"hotel_moxy", name:"Moxy Hotel",  type:"partner",    rate:30, minRate:30, maxPerDay:8,  maxPerMonth:150, bk:18,  rv:540,  minPctBk:0,  active:true,  blockWeekend:false, note:"Nearby hotel overflow partner"},
  ]);
  const [activeTab, setActiveTab] = useState("aggregators");
  const updateAgg = (id,key,val) => setAggregators(prev=>prev.map(a=>a.id===id?{...a,[key]:val}:a));
  const totalDrivoBk = DRIVO_SOURCES.reduce((a,s)=>a+s.bk,0);
  const totalDrivoRv = DRIVO_SOURCES.reduce((a,s)=>a+s.rv,0);
  const bestConv    = [...DRIVO_SOURCES].filter(s=>s.conv).sort((a,b)=>b.conv-a.conv)[0];
  const bestUpsell  = [...DRIVO_SOURCES].sort((a,b)=>b.upsells-a.upsells)[0];
  const bestTime    = [...DRIVO_SOURCES].filter(s=>s.timeOnPage).sort((a,b)=>b.timeOnPage-a.timeOnPage)[0];
  const aggByRev    = [...aggregators].sort((a,b)=>b.rv-a.rv);
  const accentColors= ["#241C8A","#3127B8","#3127B8","#3EAC4D","#3127B8","#3127B8"];
  return (
    <Box className={cls.root}>
      <Box className={cls.kpiGrid}>
        <Kpi label="DRIVO Bookings" value={totalDrivoBk.toString()} delta={14.1} up={true} period={period} />
        <Kpi label="DRIVO Revenue"  value={"$"+totalDrivoRv.toLocaleString()} delta={18.2} up={true} period={period} />
        <Kpi label="Aggregator Bk" value={aggregators.filter(a=>a.type==="aggregator").reduce((s,a)=>s+a.bk,0).toString()} delta={3.1} up={true} period={period} />
        <Kpi label="Partner Hotels" value={aggregators.filter(a=>a.type==="partner").reduce((s,a)=>s+a.bk,0).toString()} delta={6.8} up={true} period={period} />
      </Box>
      <Box className={cls.tabStrip}>
        {[["aggregators","Aggregators & Partners"],["drivo","DRIVO Booking Sources"]].map(([id,label])=>(
          <Button key={id} size="small" disableElevation onClick={()=>setActiveTab(id)} className={cls.tabBtn} style={{fontWeight:activeTab===id?600:400,color:activeTab===id?"#fff":"var(--color-text-secondary)",background:activeTab===id?P.nv:"transparent"}}>{label}</Button>
        ))}
      </Box>
      {activeTab==="drivo"&&(
        <Box className={cls.drivoList}>
          <Box className={cls.drivoHeaderRow}>
            <Typography variant="body2" component="div" className={cls.drivoHeaderTitle}>DRIVO Booking Sources</Typography>
            <Typography variant="body2" component="div" className={cls.drivoHeaderInfo}>Best conversion: <span className={cls.drivoHeaderHi}>{bestConv.name} ({bestConv.conv}%)</span>{" . "}Longest engagement: <span className={cls.drivoHeaderHi}>{bestTime.name} ({bestTime.timeOnPage}min)</span>{" . "}Most upsells: <span className={cls.drivoHeaderHi}>{bestUpsell.name} ({bestUpsell.upsells})</span></Typography>
          </Box>
          {[...DRIVO_SOURCES].sort((a,b)=>b.rv-a.rv).map((s,i)=>(
            <Paper key={s.id} elevation={0} className={cls.drivoCard}>
              <Box className={cls.drivoGrid}>
                <div style={{background:accentColors[i]}} />
                <Box className={cls.drivoBody}>
                  <Box className={cls.drivoTopRow}>
                    <div>
                      <Box className={cls.drivoNameRow}>
                        <Typography variant="body2" component="span" className={cls.drivoName}>{s.name}</Typography>
                        {s.upsells===bestUpsell.upsells&&<Typography variant="caption" component="span" className={`${cls.drivoBadge} ${cls.badgeNv}`}>TOP UPSELLS</Typography>}
                        {s.conv&&s.conv===bestConv.conv&&<Typography variant="caption" component="span" className={`${cls.drivoBadge} ${cls.badgePd}`}>BEST CONVERSION</Typography>}
                        {s.timeOnPage&&s.timeOnPage===bestTime.timeOnPage&&<Typography variant="caption" component="span" className={`${cls.drivoBadge} ${cls.badgeBl}`}>MOST ENGAGED</Typography>}
                      </Box>
                      <Typography variant="body2" component="div" className={cls.drivoDesc}>{s.desc}</Typography>
                    </div>
                    <Box className={cls.drivoRevCol}>
                      <Typography variant="caption" component="div" className={cls.drivoRevLabel}>Revenue</Typography>
                      <Typography variant="h6" component="div" className={cls.drivoRevVal}>{"$"+s.rv.toLocaleString()}</Typography>
                      <Typography variant="caption" component="div" className={cls.drivoRevChg}>{"+"+s.chg+"% vs prior"}</Typography>
                    </Box>
                  </Box>
                  <Box className={cls.drivoStatGrid}>
                    {[["Bookings",s.bk,false],["Avg Booking","$"+s.avg,false],["Upsells",s.upsells,s.upsells===bestUpsell.upsells],["Upsell Rev","$"+s.upsellRev.toLocaleString(),false],["Conversion",s.conv?s.conv+"%":"--",s.conv&&s.conv===bestConv.conv],["Time on Page",s.timeOnPage?s.timeOnPage+"min":"--",s.timeOnPage&&s.timeOnPage===bestTime.timeOnPage]].map(([label,val,hl])=>(
                      <Box key={label} className={cls.drivoStatTile} style={{border:hl?"0.5px solid #BFE3C6":"0.5px solid var(--color-border-tertiary)"}}>
                        <Typography variant="caption" component="div" className={cls.drivoStatLabel}>{label}</Typography>
                        <Typography variant="body2" component="div" className={cls.drivoStatVal} style={{color:hl?"#241C8A":P.headline}}>{val}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      )}
      {activeTab==="aggregators"&&(
        <Box className={cls.aggGrid}>
          <Card title="Channel Revenue Ranking" subtitle="All channels ranked by revenue this period">
            {aggByRev.map((a,i)=>{
              const minPctFlag=a.type==="aggregator"&&a.minPctBk>=80;
              return (
                <Box key={a.id} className={cls.rankRow}>
                  <Box className={cls.rankTopRow}>
                    <Box className={cls.rankLeft}>
                      <Typography variant="body2" component="span" className={cls.rankNum} style={{color:i===0?P.nv:"var(--color-text-tertiary)"}}>{i+1}</Typography>
                      <div>
                        <Box className={cls.rankNameRow}>
                          <Typography variant="body2" component="span" className={cls.rankName}>{a.name}</Typography>
                          <Typography variant="caption" component="span" className={cls.rankTag} style={{background:a.type==="partner"?"#3127B8":P.nv}}>{a.type==="partner"?"PARTNER":"AGGREGATOR"}</Typography>
                          {!a.active&&<Typography variant="caption" component="span" className={cls.rankPaused}>PAUSED</Typography>}
                          {a.blockWeekend&&<Typography variant="caption" component="span" className={cls.rankWeekend}>BLOCKS WEEKENDS</Typography>}
                        </Box>
                        <Typography variant="caption" component="div" className={cls.rankNote}>{a.note}</Typography>
                      </div>
                    </Box>
                    <Box className={cls.rankRevCol}>
                      <Typography variant="subtitle1" component="div" className={cls.rankRevVal}>{"$"+a.rv.toLocaleString()}</Typography>
                      <Typography variant="caption" component="div" className={cls.rankRevSub}>{a.bk+" bk . $"+a.rate+"/hr"}</Typography>
                    </Box>
                  </Box>
                  <Box className={cls.rankBarTrack}>
                    <div className={cls.rankBarFill} style={{width:Math.round(a.rv/aggByRev[0].rv*100)+"%"}} />
                  </Box>
                  {minPctFlag&&<Box className={cls.rankFlag}><Typography variant="caption" component="span" className={cls.rankFlagText}>{a.minPctBk}% at minimum rate (${a.minRate}) -- consider raising floor</Typography></Box>}
                </Box>
              );
            })}
          </Card>
          <Box className={cls.rightCol}>
            <Card title="Aggregator Inventory & Rates" subtitle="Set pricing, inventory and weekend rules">
              {aggregators.filter(a=>a.type==="aggregator").map((a)=>(
                <Box key={a.id} className={cls.aggItem}>
                  <Box className={cls.aggHeaderRow}>
                    <Typography variant="body2" component="span" className={cls.aggName}>{a.name}</Typography>
                    <Box className={cls.aggControls}>
                      <label className={cls.weekendLabel}>
                        <Checkbox size="small" className={cls.checkbox} checked={a.blockWeekend} onChange={e=>updateAgg(a.id,"blockWeekend",e.target.checked)} />
                        <Typography variant="caption" component="span" className={cls.weekendText}>Block weekends</Typography>
                      </label>
                      <Button size="small" disableElevation variant="outlined" onClick={()=>updateAgg(a.id,"active",!a.active)} className={cls.toggleBtn} style={{border:"0.5px solid "+(a.active?"#3EAC4D":P.nv),background:a.active?"#E6F4E8":P.nv,color:a.active?"#2E7D32":"#fff"}}>{a.active?"Active":"Paused"}</Button>
                    </Box>
                  </Box>
                  <Box className={cls.sliderGrid2}>
                    {[["Rate ($/hr)",a.rate,"rate",a.minRate,60],["Min Rate ($/hr)",a.minRate,"minRate",8,a.rate]].map(([label,val,key,min,max])=>(
                      <div key={key}>
                        <Box className={cls.sliderHeadRow}>
                          <Typography variant="caption" component="label" className={cls.sliderLabel}>{label}</Typography>
                          <Typography variant="body2" component="span" className={cls.sliderVal}>{"$"+val}</Typography>
                        </Box>
                        <Slider className={cls.slider} min={min} max={max} step={1} value={val} onChange={(e,v)=>updateAgg(a.id,key,+v)} />
                      </div>
                    ))}
                  </Box>
                  <Box className={cls.sliderGrid2b}>
                    {[["Max/day",a.maxPerDay,"maxPerDay",1,30,1],["Max/month",a.maxPerMonth,"maxPerMonth",10,500,5]].map(([label,val,key,min,max,step])=>(
                      <div key={key}>
                        <Box className={cls.sliderHeadRow}>
                          <Typography variant="caption" component="label" className={cls.sliderLabel}>{label}</Typography>
                          <Typography variant="body2" component="span" className={cls.sliderVal}>{val}</Typography>
                        </Box>
                        <Slider className={cls.slider} min={min} max={max} step={step} value={val} onChange={(e,v)=>updateAgg(a.id,key,+v)} />
                      </div>
                    ))}
                  </Box>
                </Box>
              ))}
            </Card>
            <Card title="Partner Hotels" subtitle="Nearby hotels routing overflow parking here">
              {aggregators.filter(a=>a.type==="partner").map((a)=>(
                <Box key={a.id} className={cls.partnerItem}>
                  <Box className={cls.partnerHeaderRow}>
                    <div>
                      <Typography variant="body2" component="div" className={cls.partnerName}>{a.name}</Typography>
                      <Typography variant="caption" component="div" className={cls.partnerSub}>{a.bk+" bookings . $"+a.rv.toLocaleString()+" revenue"}</Typography>
                    </div>
                    <Box className={cls.partnerControls}>
                      <label className={cls.weekendLabel}>
                        <Checkbox size="small" className={cls.checkbox} checked={a.blockWeekend} onChange={e=>updateAgg(a.id,"blockWeekend",e.target.checked)} />
                        <Typography variant="caption" component="span" className={cls.weekendText}>Block weekends</Typography>
                      </label>
                      <Button size="small" disableElevation variant="outlined" onClick={()=>updateAgg(a.id,"active",!a.active)} className={cls.toggleBtn} style={{border:"0.5px solid "+(a.active?"#3EAC4D":P.nv),background:a.active?"#E6F4E8":P.nv,color:a.active?"#2E7D32":"#fff"}}>{a.active?"Active":"Paused"}</Button>
                    </Box>
                  </Box>
                  <Box className={cls.sliderGrid3}>
                    {[["Rate ($/hr)",a.rate,"rate",15,80,1],["Max/day",a.maxPerDay,"maxPerDay",1,30,1],["Max/month",a.maxPerMonth,"maxPerMonth",10,300,5]].map(([label,val,key,min,max,step])=>(
                      <div key={key}>
                        <Box className={cls.sliderHeadRow}>
                          <Typography variant="caption" component="label" className={cls.sliderLabel}>{label}</Typography>
                          <Typography variant="body2" component="span" className={cls.sliderVal}>{key==="rate"?"$"+val:val}</Typography>
                        </Box>
                        <Slider className={cls.slider} min={min} max={max} step={step} value={val} onChange={(e,v)=>updateAgg(a.id,key,+v)} />
                      </div>
                    ))}
                  </Box>
                </Box>
              ))}
              <Button size="small" disableElevation variant="outlined" className={cls.addBtn}>+ Add Partner Hotel</Button>
            </Card>
          </Box>
        </Box>
      )}
    </Box>
  );
}
